/**
 * Global search (tracker item 61) helpers: the app route of a result and the
 * shortcut hint shown on the search buttons.
 */
import { GlobalSearchResult } from '../api/types';

/**
 * The route a result opens. The frontend owns its routes (see App.tsx), so
 * this is built from the type and ids rather than trusting `result.path`;
 * a property opens its customer-scoped detail page (tracker item 54).
 */
export function globalSearchResultPath(result: Pick<GlobalSearchResult, 'type' | 'id' | 'customerId'>): string {
  const id = encodeURIComponent(result.id);
  switch (result.type) {
    case 'customer':
      return `/customers/${id}`;
    case 'property':
      return result.customerId ? `/customers/${encodeURIComponent(result.customerId)}/properties/${id}` : `/properties/${id}`;
    case 'job':
      return `/jobs/${id}`;
    case 'invoice':
      return `/invoices/${id}`;
    case 'payment':
      return `/payments/${id}`;
    case 'estimate':
      return `/estimates/${id}`;
    default:
      return '/';
  }
}

/** True on Apple platforms, where the shortcut is Cmd-K rather than Ctrl-K. */
export function isApplePlatform(nav: { platform?: string; userAgent?: string } | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  const text = `${nav?.platform ?? ''} ${nav?.userAgent ?? ''}`;
  return /Mac|iPhone|iPad|iPod/i.test(text);
}

/** "⌘K" on Apple platforms, else "Ctrl K". */
export function globalSearchShortcutLabel(apple: boolean = isApplePlatform()): string {
  return apple ? '⌘K' : 'Ctrl K';
}

/** Ctrl-K or Cmd-K, without Alt or Shift. */
export function isGlobalSearchShortcut(event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): boolean {
  return (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k';
}
